import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import FriendAvatar from '../users/FriendAvatar';
import { memorySharePath } from '../../services/memories';
import './NotificationsModal.css';

function isQuietHistory(item) {
  return (
    item?.status === 'read' &&
    (item.kind === 'memory_invite'
      || item.kind === 'friend_accepted'
      || item.kind === 'event_day'
      || item.kind === 'giveaway_win'
      || item.kind === 'giveaway_instructions')
  );
}

function eventDayLine(item) {
  const title = item?.eventTitle?.trim() || 'Saved event';
  const place = item?.location?.trim();
  return place ? `Today · ${title} · ${place}` : `Today · ${title}`;
}

function giveawayLine(item) {
  const body = item?.body?.trim();
  if (body) return body;
  if (item?.kind === 'giveaway_instructions') return 'Your giveaway tickets';
  const title = item?.eventTitle?.trim() || 'a prize';
  return `You won ${title}`;
}

/**
 * Header notifications modal — friend requests, accepted requests, memory invites, event-day reminders.
 */
export default function NotificationsModal({
  items = [],
  loading = false,
  busyId = null,
  onRespondFriend,
  onOpenMemory,
  onDismissMemory,
  onOpenChat,
  onMarkChatRead,
  onClose,
  showPushCta = false,
  notices = [],
  showInstallNotice = false,
  installOffer = null,
}) {
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  return (
    <div className="friend-notif-modal-overlay" onClick={onClose} role="presentation">
      <div
        className="friend-notif-modal-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="notif-modal-heading"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="friend-notif-modal-head">
          <h2 id="notif-modal-heading">Notifications</h2>
          <button
            type="button"
            className="friend-notif-modal-close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        {showPushCta ? (
          <p className="friend-notif-modal-push pub-mono">
            Lock-screen alerts live on Profile.{' '}
            <Link to="/profile#alerts" onClick={onClose}>
              Turn on
            </Link>
          </p>
        ) : null}

        {showInstallNotice || notices.length > 0 ? (
          <ul className="friend-notif-modal-list notif-notices">
            {showInstallNotice ? (
              <li className="friend-notif-modal-row notif-notice">
                <p className="notif-notice__title">Install MapToBe</p>
                <p className="notif-notice__body">
                  Installing lets MapToBe alert you for messages, friend requests, and saved events.
                </p>
              </li>
            ) : null}
            {notices.map((notice) => (
              <li key={notice.id} className="friend-notif-modal-row notif-notice">
                <p className="notif-notice__title">{notice.title}</p>
                <p className="notif-notice__body">{notice.body}</p>
              </li>
            ))}
          </ul>
        ) : null}

        {loading ? (
          <p className="friend-notif-modal-empty pub-mono">Loading…</p>
        ) : items.length === 0 ? (
          showInstallNotice || notices.length > 0 ? null : (
            <p className="friend-notif-modal-empty pub-mono">No notifications</p>
          )
        ) : (
          <ul className="friend-notif-modal-list">
            {items.map((item) => {
              if (isQuietHistory(item) && item.kind === 'memory_invite') {
                const title = item.memoryTitle?.trim() || 'a memory';
                return (
                  <li
                    key={item.id}
                    className="friend-notif-modal-row friend-notif-modal-row--history"
                  >
                    <div className="friend-notif-modal-identity">
                      <FriendAvatar photoURL={item.fromPhotoURL} name={item.fromLabel} />
                      <span className="friend-notif-modal-name">
                        {item.fromLabel} invited you to {title}
                      </span>
                    </div>
                  </li>
                );
              }

              if (isQuietHistory(item) && item.kind === 'friend_accepted') {
                return (
                  <li
                    key={item.id}
                    className="friend-notif-modal-row friend-notif-modal-row--history"
                  >
                    <div className="friend-notif-modal-identity">
                      <FriendAvatar photoURL={item.fromPhotoURL} name={item.fromLabel} />
                      <span className="friend-notif-modal-name">
                        {item.fromLabel} accepted your friend request
                      </span>
                    </div>
                  </li>
                );
              }

              if (item.kind === 'memory_invite') {
                const memoryPath = memorySharePath(item.memoryId);
                const title = item.memoryTitle?.trim() || 'a memory';
                return (
                  <li key={item.id} className="friend-notif-modal-row">
                    <div className="friend-notif-modal-identity">
                      <FriendAvatar photoURL={item.fromPhotoURL} name={item.fromLabel} />
                      <span className="friend-notif-modal-name">
                        {item.fromLabel} invited you to {title}
                      </span>
                    </div>
                    <div className="friend-notif-modal-actions">
                      <Link
                        to={memoryPath}
                        className="me-btn"
                        onClick={() => {
                          onOpenMemory?.(item);
                          onClose();
                        }}
                      >
                        Open
                      </Link>
                      <button
                        type="button"
                        className="me-btn me-btn--ghost"
                        disabled={Boolean(busyId)}
                        onClick={() => onDismissMemory?.(item)}
                      >
                        {busyId === item.id ? '…' : 'Dismiss'}
                      </button>
                    </div>
                  </li>
                );
              }

              if (item.kind === 'friend_accepted') {
                const viewPath = item.fromUsername
                  ? `/${item.fromUsername}`
                  : '/friends';
                return (
                  <li key={item.id} className="friend-notif-modal-row">
                    <div className="friend-notif-modal-identity">
                      <FriendAvatar photoURL={item.fromPhotoURL} name={item.fromLabel} />
                      <span className="friend-notif-modal-name">
                        {item.fromLabel} accepted your friend request
                      </span>
                    </div>
                    <div className="friend-notif-modal-actions">
                      <Link
                        to={viewPath}
                        className="me-btn me-btn--ghost friend-notif-modal-view"
                        onClick={() => {
                          onDismissMemory?.(item);
                          onClose();
                        }}
                      >
                        View
                      </Link>
                      <button
                        type="button"
                        className="me-btn me-btn--ghost"
                        disabled={Boolean(busyId)}
                        onClick={() => onDismissMemory?.(item)}
                      >
                        {busyId === item.id ? '…' : 'Dismiss'}
                      </button>
                    </div>
                  </li>
                );
              }

              if (isQuietHistory(item) && item.kind === 'event_day') {
                return (
                  <li
                    key={item.id}
                    className="friend-notif-modal-row friend-notif-modal-row--history"
                  >
                    <div className="friend-notif-modal-identity">
                      <span className="friend-notif-modal-name">{eventDayLine(item)}</span>
                    </div>
                  </li>
                );
              }

              if (item.kind === 'event_day') {
                const eventPath = item.eventId ? `/events/${item.eventId}` : '/';
                return (
                  <li key={item.id} className="friend-notif-modal-row">
                    <div className="friend-notif-modal-identity">
                      <span className="friend-notif-modal-name">{eventDayLine(item)}</span>
                    </div>
                    <div className="friend-notif-modal-actions">
                      <Link
                        to={eventPath}
                        className="me-btn"
                        onClick={() => {
                          onOpenMemory?.(item);
                          onClose();
                        }}
                      >
                        Open
                      </Link>
                      <button
                        type="button"
                        className="me-btn me-btn--ghost"
                        disabled={Boolean(busyId)}
                        onClick={() => onDismissMemory?.(item)}
                      >
                        {busyId === item.id ? '…' : 'Dismiss'}
                      </button>
                    </div>
                  </li>
                );
              }

              if (item.kind === 'giveaway_win' || item.kind === 'giveaway_instructions') {
                return (
                  <li key={item.id} className="friend-notif-modal-row">
                    <div className="friend-notif-modal-identity">
                      <span className="friend-notif-modal-name">{giveawayLine(item)}</span>
                    </div>
                    <div className="friend-notif-modal-actions">
                      <Link
                        to="/giveaway"
                        className="me-btn"
                        onClick={() => {
                          onOpenMemory?.(item);
                          onClose();
                        }}
                      >
                        Open
                      </Link>
                      <button
                        type="button"
                        className="me-btn me-btn--ghost"
                        disabled={Boolean(busyId)}
                        onClick={() => onDismissMemory?.(item)}
                      >
                        {busyId === item.id ? '…' : 'Dismiss'}
                      </button>
                    </div>
                  </li>
                );
              }

              if (item.kind === 'chat_message') {
                const preview = item.preview || 'New message';
                return (
                  <li key={item.id} className="friend-notif-modal-row">
                    <div className="friend-notif-modal-identity">
                      <FriendAvatar photoURL={item.fromPhotoURL} name={item.fromLabel} />
                      <span className="friend-notif-modal-name">
                        {item.fromLabel}: {preview}
                      </span>
                    </div>
                    <div className="friend-notif-modal-actions">
                      <Link
                        to={`/friends/chat/${item.chatId}`}
                        className="me-btn"
                        onClick={() => {
                          onOpenChat?.(item);
                          onClose();
                        }}
                      >
                        Open
                      </Link>
                      <button
                        type="button"
                        className="me-btn me-btn--ghost"
                        disabled={Boolean(busyId)}
                        onClick={() => onMarkChatRead?.(item)}
                      >
                        {busyId === item.id ? '…' : 'Mark read'}
                      </button>
                    </div>
                  </li>
                );
              }

              const profilePath = item.fromUsername ? `/${item.fromUsername}` : null;
              const identity = (
                <>
                  <FriendAvatar photoURL={item.fromPhotoURL} name={item.fromLabel} />
                  <span className="friend-notif-modal-name">{item.fromLabel}</span>
                </>
              );
              return (
                <li key={item.id} className="friend-notif-modal-row">
                  {profilePath ? (
                    <Link
                      to={profilePath}
                      className="friend-notif-modal-identity"
                      onClick={onClose}
                    >
                      {identity}
                    </Link>
                  ) : (
                    <div className="friend-notif-modal-identity">{identity}</div>
                  )}
                  <div className="friend-notif-modal-actions">
                    {profilePath ? (
                      <Link
                        to={profilePath}
                        className="me-btn me-btn--ghost friend-notif-modal-view"
                        onClick={onClose}
                      >
                        View
                      </Link>
                    ) : null}
                    <button
                      type="button"
                      className="me-btn"
                      disabled={Boolean(busyId)}
                      onClick={() => onRespondFriend(item.id, true)}
                    >
                      {busyId === item.id ? '…' : 'Accept'}
                    </button>
                    <button
                      type="button"
                      className="me-btn me-btn--ghost"
                      disabled={Boolean(busyId)}
                      onClick={() => onRespondFriend(item.id, false)}
                    >
                      Decline
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
        {installOffer}
      </div>
    </div>
  );
}
